import * as cacheManager from "../cacheManager";
import * as targetDisplayValues from "../../enums/targetDisplayValues";
import cacheEnhancer from "./cacheEnhancer";
import instance from "./instance";
import uuid from "uuid";

function generateIds(arr) {
	return arr.map(elem => ({ ...elem, id: uuid.v4() }));
}

function parseFilter(f) {
	if (f.children) {
		return {
			...f,
			id: uuid.v4(),
			children: generateIds(f.children),
		};
	}
	return {
		...f,
		id: uuid.v4(),
	};
}

function parseFilters(filtersArr) {
	return filtersArr.map(parseFilter);
}

function parseField(field, libraryName) {
	let subfields = field.subfields;
	if (Array.isArray(subfields)) {
		subfields = subfields.map(f => parseField(f, libraryName));
	}
	return {
		...field,
		library_name: libraryName,
		subfields,
	};
}

function parseTarget(target) {
	return {
		...target,
		fields: target.fields.map(field => parseField(field, target.library_name)),
	};
}

export function targets(libraryName) {
	return instance
		.request({
			url: "/query/targets",
			method: "POST",
			data: {
				library_name: libraryName,
			},
		})
		.then(response => response.data.map(parseTarget));
}

function fetchEditors() {
	return instance
		.request({
			url: "/query/editors",
			method: "POST",
		})
		.then(response => response.data);
}

export const editors = cacheEnhancer(fetchEditors, "editors");

export function networkGrid(search, orders, start, limit) {
	return instance
		.request({
			url: "/query/grid",
			method: "POST",
			data: {
				search,
				orders,
				start,
				limit,
			},
		})
		.then(response => response.data);
}

export function grid(search, orders, start, limit, binderId, tabId, tileId) {
	const key = cacheManager.getSearchGridPageKey(binderId, tabId, tileId, start, limit);
	return cacheEnhancer(networkGrid, key, 600, true)(search, orders, start, limit);
}

export function count(search, signal) {
	return instance
		.request({
			url: "/query/count",
			method: "POST",
			data: {
				search,
			},
			signal: signal,
		})
		.then(response => response.data);
}

export function countCache(search, binderId, tabId) {
	return cacheEnhancer(count, `count-${binderId}-${tabId}`, 600, true)(search);
}

export function summary(search) {
	return instance
		.request({
			url: "/query/summary",
			method: "POST",
			data: {
				search,
			},
		})
		.then(response => response.data);
}

export function table(search) {
	return instance
		.request({
			url: "/query/table",
			method: "POST",
			data: {
				search,
			},
		})
		.then(response => response.data);
}

export function related(search) {
	return instance
		.request({
			url: "/query/related",
			method: "POST",
			data: {
				search,
			},
		})
		.then(response => generateIds(response.data));
}

function networkFilters(search, memo) {
	const data = {
		search,
	};
	if (memo) {
		data.memo = memo;
	}
	return instance
		.request({
			url: "/query/filters",
			method: "POST",
			data,
		})
		.then(response => parseFilters(response.data));
}

export function filters(search, memo, binderId, tabId) {
	if (memo || !binderId || !tabId) {
		return networkFilters(search, memo);
	}
	return cacheEnhancer(networkFilters, `filters-${binderId}-${tabId}`, 600, true)(search);
}

export function joinpaths(target_name, criterion_name) {
	return instance
		.request({
			url: "/query/joinpaths",
			method: "POST",
			data: {
				target_name,
				criterion_name,
			},
		})
		.then(response => response.data);
}

export function binder(search) {
	return instance
		.request({
			url: "/query/binder",
			method: "POST",
			data: {
				search,
			},
		})
		.then(response => response.data);
}

export const reports = cacheEnhancer(networkReports, "reports", 600, true);

export function networkReports() {
	return instance
		.request({
			url: "/query/reports",
			method: "POST",
		})
		.then(response =>
			response.data.map(report => ({
				...report,
				comparison: report.display === targetDisplayValues.COMPARISON,
			})),
		);
}

export function tiles(search, sectionName, tileControls) {
	return instance
		.request({
			url: "/query/tiles",
			method: "POST",
			data: {
				search,
				section_name: sectionName,
				controls: tileControls,
			},
		})
		.then(response => {
			return {
				...response.data,
				tiles: response.data.tiles.map(tile => ({
					...tile,
					controls: tile.controls
						? generateIds(tile.controls).map(controlGroup => ({
								...controlGroup,
								radio_buttons: generateIds(controlGroup.radio_buttons),
						  }))
						: undefined,
				})),
			};
		});
}

export function describe(search) {
	return instance
		.request({
			url: "/query/describe",
			method: "POST",
			data: {
				search,
			},
		})
		.then(response => response.data);
}

export function legend(targetName) {
	return cacheEnhancer(networkLegend, `grid-${targetName}`, 600, true)(targetName);
}

export function networkLegend(targetName) {
	return instance
		.request({
			url: "/query/legend",
			method: "POST",
			data: {
				target_name: targetName,
			},
		})
		.then(response => response.data);
}

export function controls(search, memo) {
	const data = { search };
	if (memo) {
		data.memo = memo;
	}
	return instance
		.request({
			url: "/query/controls",
			method: "POST",
			data,
		})
		.then(response =>
			generateIds(response.data)
				.map(tabControl => {
					if (tabControl.options) {
						return {
							...tabControl,
							options: generateIds(tabControl.options).map(opt => ({
								...opt,
								filter: opt.filter.map(f => parseFilter(f)),
							})),
						};
					} else {
						// NOTE: temporary fix to prevent old control format from breaking site
						return null;
					}
				})
				.filter(tabControl => !!tabControl),
		);
}
